/*
 * globo-size-bridge.js
 * Keeps GLO Color Swatch's Size buttons in sync with the theme's (hidden) variant-picker.
 *
 * Problem it fixes: Globo renders Size buttons but never passes size clicks to the theme,
 * so the selected variant / SKU / cart item stay on the previous size. Globo also shows
 * sold-out sizes as clickable.
 *
 * What it does:
 *  1. Globo size click  -> checks the matching theme size radio and fires `change`
 *     (the theme then updates variant ID, SKU, price, URL as normal).
 *  2. Sizes the theme marks unavailable -> disabled + hidden in Globo.
 *  3. Theme selection changes (incl. variant-size-fallback.js) -> Globo highlight follows.
 *  4. Size buttons are always shown in size order (XXS, XS, S, M, L, XL, XXL, XXXL...).
 *  5. Selected size is plain solid black (no extra outline ring).
 *
 * Install: Online Store > Themes > (duplicate first) > Edit code > Assets > add this file,
 * then in layout/theme.liquid, after variant-size-fallback.js:
 *   <script src="{{ 'globo-size-bridge.js' | asset_url }}" defer></script>
 */
(() => {
  if (window.__gloSizeBridge) return;
  window.__gloSizeBridge = true;

  const SIZE_RE = /size/i;
  const UNAVAILABLE_CLASS = 'gsb-unavailable';

  // Hide unavailable Globo size buttons
  const style = document.createElement('style');
  // Globo's own sold-out flags (.globo-out-of-stock) can land on the wrong sizes when the
  // product's stored size order differs from the displayed order, so size buttons ignore
  // them and use the theme's availability instead.
  style.textContent = `
    li.gsb-size.globo-out-of-stock:not(.${UNAVAILABLE_CLASS}) { display: block !important; }
    li.gsb-size.${UNAVAILABLE_CLASS} { display: none !important; }
    li.gsb-size .attr-title-hover { display: none !important; }
    li.gsb-size label.globo-style--button { outline: none !important; }
    li.gsb-size input:focus-visible + label { outline: 2px solid #000 !important; outline-offset: 2px; }
  `;
  document.head.appendChild(style);

  const textOf = (el) => (el ? el.textContent : '').replace(/\s+/g, ' ').trim();

  // Find the theme picker + which option position is Size, relative to a given element
  function getContext(el) {
    const scope =
      (el && el.closest('.product, .quick-buy, [id^="shopify-section"]')) || document;
    const picker =
      scope.querySelector('variant-picker[form-id]') ||
      document.querySelector('variant-picker[form-id]');
    if (!picker) return null;
    const fieldsets = Array.from(picker.querySelectorAll('fieldset'));
    const sizeIdx = fieldsets.findIndex((f) =>
      SIZE_RE.test(textOf(f.querySelector('legend')))
    );
    if (sizeIdx < 0) return null;
    return { scope, picker, sizeIdx, formId: picker.getAttribute('form-id') };
  }

  function nativeSizeInputs(ctx) {
    return Array.from(
      document.querySelectorAll(
        `input[type="radio"][data-option-position="${ctx.sizeIdx + 1}"][form="${CSS.escape(ctx.formId)}"]`
      )
    );
  }

  function globoSizeInputs(ctx) {
    return Array.from(
      ctx.scope.querySelectorAll(`input[name="globo-option-${ctx.sizeIdx}"]`)
    );
  }

  const nativeLabel = (input) => textOf(input.labels && input.labels[0]);

  // Size ordering: XXS < XS < S < M < L < XL < XXL < XXXL < 4XL ...
  // Youth sizes (YS, Youth M...) come before adult; numeric sizes sort by number;
  // anything unrecognised keeps its existing order at the end.
  function sizeRank(value) {
    const s = String(value)
      .toUpperCase()
      .replace(/\(.*?\)/g, '')
      .replace(/[\s\-_.\/]/g, '')
      .replace(/^YOUTH/, 'Y')
      .replace(/EXTRA/g, 'X')
      .replace(/SMALL$/, 'S')
      .replace(/MEDIUM$/, 'M')
      .replace(/LARGE$/, 'L');
    const m = s.match(/^(Y)?(\d+)?(X*)([SML])$/);
    if (m) {
      const [, youth, digits, xs, letter] = m;
      const xCount = digits ? parseInt(digits, 10) : xs.length;
      const base = letter === 'M' ? 0 : letter === 'S' ? -(1 + xCount) : 1 + xCount;
      return (youth ? -100 : 0) + base;
    }
    const num = parseFloat(String(value).replace(',', '.'));
    return isNaN(num) ? Infinity : 1000 + num;
  }

  function compareSizes(a, b) {
    const ra = sizeRank(a);
    const rb = sizeRank(b);
    if (ra === rb) return 0;
    if (ra === Infinity) return 1;
    if (rb === Infinity) return -1;
    return ra - rb;
  }

  // Put Globo's size buttons in size order (only moves them when out of order)
  function sortGloboSizes(ctx) {
    const byParent = new Map();
    globoSizeInputs(ctx).forEach((g) => {
      const item = g.closest('li');
      if (!item || !item.parentElement) return;
      if (!byParent.has(item.parentElement)) byParent.set(item.parentElement, []);
      byParent.get(item.parentElement).push({ item, value: g.value });
    });
    byParent.forEach((entries, parent) => {
      const sorted = [...entries].sort((a, b) => compareSizes(a.value, b.value));
      if (sorted.every((e, i) => e === entries[i])) return;
      sorted.forEach((e) => parent.appendChild(e.item));
    });
  }

  function findNative(ctx, value) {
    return nativeSizeInputs(ctx).find((i) => nativeLabel(i) === value);
  }

  // Mirror theme state (checked + available) onto Globo's size buttons
  function syncToGlobo(ctx) {
    if (!ctx) return;
    const natives = nativeSizeInputs(ctx);
    if (!natives.length) return;
    globoSizeInputs(ctx).forEach((g) => {
      const n = natives.find((i) => nativeLabel(i) === g.value);
      const unavailable = !n || n.disabled;
      const item = g.closest('li') || g.parentElement;
      if (item) {
        item.classList.add('gsb-size');
        item.classList.toggle(UNAVAILABLE_CLASS, unavailable);
      }
      g.disabled = unavailable;
      if (n && n.checked && !g.checked) g.checked = true;
    });
    // Keep Globo's "Size: X" label in step with the selected size
    const checked = globoSizeInputs(ctx).find((g) => g.checked);
    const box = checked && checked.closest('.swatch--gl');
    const nameEl = box && box.querySelector('.name-variant');
    if (nameEl && nameEl.lastChild && nameEl.lastChild.nodeType === 3 && nameEl.lastChild.nodeValue !== checked.value) {
      nameEl.lastChild.nodeValue = checked.value;
    }
    sortGloboSizes(ctx);
  }

  // 1. Globo size click -> theme
  document.addEventListener(
    'change',
    (event) => {
      const g = event.target;
      if (!(g instanceof HTMLInputElement) || !/^globo-option-\d+$/.test(g.name)) return;
      const ctx = getContext(g);
      if (!ctx || g.name !== `globo-option-${ctx.sizeIdx}`) return;

      const n = findNative(ctx, g.value);
      if (!n || n.disabled) {
        // Unavailable: put Globo back on the size the theme actually has selected
        syncToGlobo(ctx);
        return;
      }
      if (!n.checked) {
        n.checked = true;
        n.dispatchEvent(new Event('change', { bubbles: true }));
      }
    },
    true
  );

  // Block clicks on unavailable Globo sizes before Globo reacts
  document.addEventListener(
    'click',
    (event) => {
      const item = event.target.closest && event.target.closest(`.${UNAVAILABLE_CLASS}`);
      if (item) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    true
  );

  // 2 + 3. Theme re-renders / selection changes -> Globo
  let timer;
  const schedule = () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      document
        .querySelectorAll('variant-picker[form-id]')
        .forEach((p) => syncToGlobo(getContext(p)));
    }, 150);
  };
  document.addEventListener('change', schedule);
  new MutationObserver(schedule).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }
})();
